import React from 'react';
import PropTypes from "prop-types";
import moment from "moment/moment";
import {Grid, Paper, Typography} from "@material-ui/core";
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import AccessTimeOutlinedIcon from '@material-ui/icons/AccessTimeOutlined';
import AlarmOutlinedIcon from '@material-ui/icons/AlarmOutlined';
import ChatOutlinedIcon from '@material-ui/icons/ChatOutlined';
import CancelIcon from '@material-ui/icons/Cancel';
import {useConfig} from "../../context/configContext";
import theme from "./styles";
import translate from "../../misc/applyTranslation";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from '@material-ui/icons/Close';

const Scheduled = props => {
  const config = useConfig();
  const classes = theme(config);

  const {lastEventsPerJob, eventsToday = []} = props;

  const [errorEventOpen, setErrorEventOpen] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  const isSuccessStatus = (statusCode) => String(statusCode) === '200';
  const getRowStatusClass = (statusCode) => (isSuccessStatus(statusCode) ? {} : classes.tableErrorRow);
  const getStatusDotClass = (statusCode) => (isSuccessStatus(statusCode) ? classes.successDot : classes.errorDot);
  const getPillStatusClass = (statusCode) => (isSuccessStatus(statusCode) ? classes.successPill : classes.errorPill);

  const getStatusCodeDescription = (statusCode) => {
    const code = Number(statusCode);

    if (code >= 400 && code < 500) {
      return `Status ${statusCode}: The service failed while processing the workflow. Check logs for more details.`;
    }
    if (code >= 500 && code < 600) {
      return `Status ${statusCode}: The service failed while processing the workflow. Check logs for more details.`;
    }

    return `Status ${statusCode || '-'}: The workflow did not complete successfully. Check logs for more details.`;
  };

  const handleErrorRowClick = (item) => {
    if (!isSuccessStatus(item.status_code)) {
      setErrorEventOpen(item)
      const latestFailedForJob = eventsToday
        .filter((event) => event.job === item.job)
        .sort((a, b) => new Date(b.end_time || b.start_time || 0) - new Date(a.end_time || a.start_time || 0))[0];
      setErrorEventOpen(latestFailedForJob || item);
    }
  };

  const copyError = () => {
    if (errorEventOpen === null) {
      return;
    }

    const text = `
    Status Code: ${errorEventOpen.status_code}
    Run ID: ${errorEventOpen.run_id}
    Name: ${(errorEventOpen.job || 'UNKNOWN').toUpperCase()}
    Message: ${getStatusCodeDescription(errorEventOpen.status_code)},
    Start Time: ${errorEventOpen.start_time ? moment(errorEventOpen.start_time).format('YYYY-MM-DD HH:mm:ss') : "-"},
    End Time: ${errorEventOpen.end_time ? moment(errorEventOpen.end_time).format('YYYY-MM-DD HH:mm:ss') : "-"}
    `.trim();

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1500);
    }).catch(() => {
    });
  };

  return (
    <Paper elevation={0} className={classes.contentSections}>
      <Grid container spacing={!errorEventOpen ? 0 : 2}>
        <Grid item xs={!errorEventOpen ? 12 : 5} className={classes.tableHeader}>
          <div className={classes.tableTitleSection}>
            <Typography variant="h5" component="h2"
              className={classes.tableTitle}>{translate("n8n", "scheduled_jobs_title")}</Typography>
            <Typography variant="subtitle2" component="h6" className={classes.tableSubtitle}>
              {translate("n8n", "scheduled_jobs_sub_title")}
            </Typography>
            <div className={`${classes.counterChip} ${classes.scheduledCounterChip}`}>
              <span>{lastEventsPerJob.length || 0}</span>
            </div>
          </div>
          <table className={classes.scheduledTable}>
            <thead>
              <tr className={classes.scheduledTableHeaderRow}>
                <th>{translate("n8n", "scheduled_jobs_table_column_title_job")}</th>
                <th>{translate("n8n", "scheduled_jobs_table_column_title_last_run")}</th>
                <th align="right">{translate("n8n", "scheduled_jobs_table_column_title_status")}</th>
              </tr>
            </thead>
            <tbody>
              {lastEventsPerJob.map(item => (
                <tr
                  className={`${classes.scheduledTableBodyRow} ${getRowStatusClass(item.status_code)}`}
                  key={item.run_id}
                  onClick={() => handleErrorRowClick(item)}
                >
                  <td>
                    <FiberManualRecordIcon className={`${classes.statusDot} ${getStatusDotClass(item.status_code)}`} />
                    <div className={classes.scheduledTableJobCellText}>
                      <Typography variant="subtitle1" component="p"
                        className={classes.jobName}>{(item.job || translate('n8n', 'unknown_label')).toUpperCase()}</Typography>
                      <Typography variant="subtitle2" component="p" className={classes.jobMeta}>Run
                        ID: {item.run_id ? item.run_id : '-'}</Typography>
                    </div>
                  </td>
                  <td>
                    <Typography variant="subtitle1" component="p"
                      className={classes.jobDate}>{moment(item.start_time).format('YYYY-MM-DD HH:mm:ss')}</Typography>
                    <Typography variant="subtitle2" component="p"
                      className={classes.jobMeta}>{moment(item.start_time).fromNow()}</Typography>
                  </td>
                  <td align="right">
                    <div
                      className={`${classes.statusPill} ${getPillStatusClass(item.status_code)}`}>{item.status_code || '-'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Grid>
        {errorEventOpen &&
          <Grid item xs={7} className={classes.failedJobWrap}>
            <div className={classes.tableTitleSection} style={{justifyContent: "space-between"}}>
              <div style={{display: "flex", alignItems: "center"}}>
                <Typography variant="h5" component="h2" className={classes.tableTitle}>
                  {translate("n8n", "failed_jobs_section_title")}
                </Typography>
                <Typography variant="subtitle2" component="h6" className={classes.tableSubtitle}>
                  {translate("n8n", "failed_jobs_section_sub_title")}
                </Typography>
                <div className={`${classes.counterChip} ${classes.errorCounterChip}`}>
                  <span>{lastEventsPerJob.length || 0}</span>
                </div>
              </div>
              <CloseIcon onClick={() => setErrorEventOpen(null)} style={{cursor: "pointer"}}/>
            </div>
            <div className={classes.failedJobContent}>
              <div className={classes.errorEventHeader}>
                <div className={classes.errorEventHeaderLeft}>
                  <div className={classes.errorEventNameSection}>
                    <CancelIcon/>
                    <Typography variant="subtitle1" component="p">{errorEventOpen.job.toUpperCase()}</Typography>
                  </div>
                  <div className={classes.errorEventMetaSection}>
                    <Typography variant="subtitle2" component="p" style={{marginLeft: "30px", marginRight: "4px"}}>
                      {`${translate("n8n", "failed_jobs_section_status_label")} :`}
                    </Typography>
                    <Typography variant="subtitle2" component="p" className={classes.errorStatusText}>
                      {errorEventOpen.status_code}
                    </Typography>
                    <Typography variant="subtitle2" component="p">
                      {`${translate("n8n", "failed_jobs_section_run_id_label")} : ${errorEventOpen.run_id ? errorEventOpen.run_id : '-'}`}
                    </Typography>
                  </div>
                </div>
                <div>
                  <button className={classes.errorEventCopyError} onClick={copyError}>
                    {!copied ?
                      <>
                        <FileCopyOutlinedIcon/>
                        {translate("n8n", "copy_error_btn")}
                      </>
                      :
                      <>
                        <CheckIcon/>
                        {translate("n8n", "copied_error_btn")}
                      </>
                    }
                  </button>
                </div>
              </div>
              <div>
                <Grid container className={classes.contentRow}>
                  <Grid item xs={2} className={classes.contentRowLabel}>
                    <AccessTimeOutlinedIcon/>
                    <Typography variant="subtitle2" component="p">
                      {translate("n8n", "failed_jobs_section_start_time_label")}
                    </Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <Typography variant="subtitle2" component="p">
                      {errorEventOpen.start_time ? moment(errorEventOpen.start_time).format('YYYY-MM-DD HH:mm:ss') : "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container className={classes.contentRow}>
                  <Grid item xs={2} className={classes.contentRowLabel}>
                    <AlarmOutlinedIcon/>
                    <Typography variant="subtitle2" component="p">
                      {translate("n8n", "failed_jobs_section_end_time_label")}
                    </Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <Typography variant="subtitle2" component="p">
                      {errorEventOpen.end_time ? moment(errorEventOpen.end_time).format('YYYY-MM-DD HH:mm:ss') : "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container className={classes.contentRow}>
                  <Grid item xs={2} className={classes.contentRowLabel}>
                    <ChatOutlinedIcon/>
                    <Typography variant="subtitle2" component="p">
                      {translate("n8n", "failed_jobs_section_message_label")}
                    </Typography>
                  </Grid>
                  <Grid item xs={10} className={classes.messageValueWrap}>
                    <Typography variant="subtitle2" component="p">
                      {getStatusCodeDescription(errorEventOpen.status_code)}
                    </Typography>
                  </Grid>
                </Grid>
              </div>
            </div>
          </Grid>
        }
      </Grid>
    </Paper>
  )
};

Scheduled.propTypes = {
  lastEventsPerJob: PropTypes.array.isRequired,
  eventsToday: PropTypes.array,
};

export default Scheduled;